import type {
  AdminLeadRecord,
  AdminPaymentRecord,
  BusinessCenterCompanyRecord,
  BusinessCenterDocumentRecord,
  BusinessCenterEntityId,
  BusinessCenterEntityReference,
  BusinessCenterParentChildLink,
  BusinessCenterPortalAccessRecord,
  BusinessCenterProjectRecord,
  BusinessCenterRelationshipBlueprint,
  BusinessCenterRoleAssignmentRecord,
  BusinessCenterWorkspaceRecord,
  BusinessCenterTaskRecord,
  BusinessCenterUserRecord,
  ClientRecord,
} from "./types";

export interface BusinessCenterUnifiedDataModel {
  companies: BusinessCenterCompanyRecord[];
  users: BusinessCenterUserRecord[];
  workspaces: BusinessCenterWorkspaceRecord[];
  leads: AdminLeadRecord[];
  clients: ClientRecord[];
  projects: BusinessCenterProjectRecord[];
  tasks: BusinessCenterTaskRecord[];
  documents: BusinessCenterDocumentRecord[];
  billingRecords: AdminPaymentRecord[];
  portalAccess: BusinessCenterPortalAccessRecord[];
  roleAssignments: BusinessCenterRoleAssignmentRecord[];
  companyLinks: BusinessCenterParentChildLink[];
  entityReferences: BusinessCenterEntityReference[];
}

export interface BusinessCenterUnifiedLookupTables {
  companiesById: Map<BusinessCenterEntityId, BusinessCenterCompanyRecord>;
  usersById: Map<BusinessCenterEntityId, BusinessCenterUserRecord>;
  workspacesById: Map<BusinessCenterEntityId, BusinessCenterWorkspaceRecord>;
  leadsById: Map<BusinessCenterEntityId, AdminLeadRecord>;
  clientsById: Map<BusinessCenterEntityId, ClientRecord>;
  projectsById: Map<BusinessCenterEntityId, BusinessCenterProjectRecord>;
  tasksById: Map<BusinessCenterEntityId, BusinessCenterTaskRecord>;
  documentsById: Map<BusinessCenterEntityId, BusinessCenterDocumentRecord>;
  billingRecordsById: Map<BusinessCenterEntityId, AdminPaymentRecord>;
  portalAccessById: Map<BusinessCenterEntityId, BusinessCenterPortalAccessRecord>;
  roleAssignmentsById: Map<BusinessCenterEntityId, BusinessCenterRoleAssignmentRecord>;
  referencesByEntityId: Map<BusinessCenterEntityId, BusinessCenterEntityReference[]>;
  childCompanyIdsByParentId: Map<BusinessCenterEntityId, BusinessCenterEntityId[]>;
  entityIdToCompanyId: Map<BusinessCenterEntityId, BusinessCenterEntityId>;
}

export const businessCenterUnifiedDataModelBlueprint: BusinessCenterRelationshipBlueprint = {
  companies: [],
  users: [],
  workspaces: [],
  roleAssignments: [],
  portalAccess: [],
  billingRecords: [],
  companyLinks: [],
  entityReferences: [],
};

export const emptyBusinessCenterUnifiedDataModel: BusinessCenterUnifiedDataModel = {
  companies: [],
  users: [],
  workspaces: [],
  leads: [],
  clients: [],
  projects: [],
  tasks: [],
  documents: [],
  billingRecords: [],
  portalAccess: [],
  roleAssignments: [],
  companyLinks: [],
  entityReferences: [],
};

export function createBusinessCenterEntityReference(
  entityType: BusinessCenterEntityReference["entityType"],
  entityId: BusinessCenterEntityId,
  companyId?: BusinessCenterEntityId | null,
  label?: string,
): BusinessCenterEntityReference {
  return { entityType, entityId, companyId: companyId || null, label };
}

export function buildBusinessCenterLookupTables(model: BusinessCenterUnifiedDataModel): BusinessCenterUnifiedLookupTables {
  const companiesById = indexById(model.companies);
  const usersById = indexById(model.users);
  const workspacesById = indexById(model.workspaces);
  const leadsById = indexById(model.leads);
  const clientsById = indexById(model.clients);
  const projectsById = indexById(model.projects);
  const tasksById = indexById(model.tasks);
  const documentsById = indexById(model.documents);
  const billingRecordsById = indexById(model.billingRecords);
  const portalAccessById = indexById(model.portalAccess);
  const roleAssignmentsById = indexById(model.roleAssignments);

  const referencesByEntityId = new Map<BusinessCenterEntityId, BusinessCenterEntityReference[]>();
  model.entityReferences.forEach((reference) => {
    const current = referencesByEntityId.get(reference.entityId) || [];
    referencesByEntityId.set(reference.entityId, [...current, reference]);
  });

  const childCompanyIdsByParentId = new Map<BusinessCenterEntityId, BusinessCenterEntityId[]>();
  model.companyLinks
    .filter((link) => link.parentEntityType === "company" && link.childEntityType === "company")
    .forEach((link) => {
      const current = childCompanyIdsByParentId.get(link.parentEntityId) || [];
      childCompanyIdsByParentId.set(link.parentEntityId, [...current, link.childEntityId]);
    });

  const entityIdToCompanyId = new Map<BusinessCenterEntityId, BusinessCenterEntityId>();
  model.companies.forEach((company) => {
    entityIdToCompanyId.set(company.id, company.parentCompanyId || company.id);
  });
  model.users.forEach((user) => {
    entityIdToCompanyId.set(user.id, user.companyId);
  });
  model.workspaces.forEach((workspace) => {
    entityIdToCompanyId.set(workspace.id, workspace.companyId);
  });
  model.leads.forEach((lead) => {
    entityIdToCompanyId.set(lead.id, resolveCompanyIdFromLead(lead, model));
  });
  model.clients.forEach((client) => {
    entityIdToCompanyId.set(client.id, resolveCompanyIdFromClient(client, model));
  });
  model.projects.forEach((project) => {
    entityIdToCompanyId.set(project.id, resolveCompanyIdFromProject(project, model));
  });
  model.tasks.forEach((task) => {
    entityIdToCompanyId.set(task.id, resolveCompanyIdFromTask(task, model));
  });
  model.documents.forEach((document) => {
    entityIdToCompanyId.set(document.id, resolveCompanyIdFromDocument(document, model));
  });
  model.billingRecords.forEach((billingRecord) => {
    entityIdToCompanyId.set(billingRecord.id, resolveCompanyIdFromBillingRecord(billingRecord, model));
  });
  model.portalAccess.forEach((portalAccess) => {
    entityIdToCompanyId.set(portalAccess.id, portalAccess.companyId);
  });
  model.roleAssignments.forEach((roleAssignment) => {
    entityIdToCompanyId.set(roleAssignment.id, roleAssignment.companyId);
  });
  model.workspaces.forEach((workspace) => {
    entityIdToCompanyId.set(workspace.id, resolveCompanyIdFromWorkspace(workspace, model));
  });

  return {
    companiesById,
    usersById,
    workspacesById,
    leadsById,
    clientsById,
    projectsById,
    tasksById,
    documentsById,
    billingRecordsById,
    portalAccessById,
    roleAssignmentsById,
    referencesByEntityId,
    childCompanyIdsByParentId,
    entityIdToCompanyId,
  };
}

export function getBusinessCenterEntityReferenceLabel(
  model: BusinessCenterUnifiedDataModel,
  reference: BusinessCenterEntityReference,
): string {
  switch (reference.entityType) {
    case "company":
      return model.companies.find((company) => company.id === reference.entityId)?.companyName || reference.label || reference.entityId;
    case "user":
      return model.users.find((user) => user.id === reference.entityId)?.fullName || reference.label || reference.entityId;
    case "workspace":
      return model.workspaces.find((workspace) => workspace.id === reference.entityId)?.workspaceLabel || reference.label || reference.entityId;
    case "workspace_template":
      return model.workspaces.find((workspace) => workspace.workspaceKey === reference.entityId)?.workspaceLabel || reference.label || reference.entityId;
    case "workspace_assignment":
      return model.workspaces.find((workspace) => workspace.id === reference.entityId)?.workspaceLabel || reference.label || reference.entityId;
    case "lead":
      return model.leads.find((lead) => lead.id === reference.entityId)?.fullName || reference.label || reference.entityId;
    case "client":
      return model.clients.find((client) => client.id === reference.entityId)?.clientName || reference.label || reference.entityId;
    case "project":
      return model.projects.find((project) => project.id === reference.entityId)?.projectName || reference.label || reference.entityId;
    case "task":
      return model.tasks.find((task) => task.id === reference.entityId)?.taskTitle || reference.label || reference.entityId;
    case "document":
      return model.documents.find((document) => document.id === reference.entityId)?.documentName || reference.label || reference.entityId;
    case "billing_record":
      return model.billingRecords.find((record) => record.id === reference.entityId)?.invoiceNumber || reference.label || reference.entityId;
    case "portal_access":
      return model.portalAccess.find((record) => record.id === reference.entityId)?.status || reference.label || reference.entityId;
    case "role":
      return model.roleAssignments.find((record) => record.id === reference.entityId)?.roleKey || reference.label || reference.entityId;
    default:
      return reference.label || reference.entityId;
  }
}

export function getBusinessCenterCompanyScopeSummary(
  model: BusinessCenterUnifiedDataModel,
  companyId: BusinessCenterEntityId,
) {
  return {
    company: model.companies.find((company) => company.id === companyId) || null,
    childCompanyIds: getBusinessCenterChildCompanyIds(model, companyId),
    userCount: model.users.filter((user) => user.companyId === companyId).length,
    workspaceCount: model.workspaces.filter((workspace) => workspace.companyId === companyId).length,
    leadCount: model.leads.filter((lead) => resolveCompanyIdFromLead(lead, model) === companyId).length,
    clientCount: model.clients.filter((client) => resolveCompanyIdFromClient(client, model) === companyId).length,
    projectCount: model.projects.filter((project) => resolveCompanyIdFromProject(project, model) === companyId).length,
    taskCount: model.tasks.filter((task) => resolveCompanyIdFromTask(task, model) === companyId).length,
    documentCount: model.documents.filter((document) => resolveCompanyIdFromDocument(document, model) === companyId).length,
    billingRecordCount: model.billingRecords.filter((record) => resolveCompanyIdFromBillingRecord(record, model) === companyId).length,
  };
}

export function getBusinessCenterChildCompanyIds(
  model: BusinessCenterUnifiedDataModel,
  companyId: BusinessCenterEntityId,
): BusinessCenterEntityId[] {
  const directChildren = model.companyLinks
    .filter((link) => link.parentEntityType === "company" && link.parentEntityId === companyId && link.childEntityType === "company")
    .map((link) => link.childEntityId);
  const linkedChildren = directChildren.length > 0 ? directChildren : model.companies.filter((company) => company.parentCompanyId === companyId).map((company) => company.id);
  return Array.from(new Set(linkedChildren));
}

export function findBusinessCenterRecordByReference(
  model: BusinessCenterUnifiedDataModel,
  reference: BusinessCenterEntityReference,
) {
  switch (reference.entityType) {
    case "company":
      return model.companies.find((record) => record.id === reference.entityId) || null;
    case "user":
      return model.users.find((record) => record.id === reference.entityId) || null;
    case "workspace":
      return model.workspaces.find((record) => record.id === reference.entityId) || null;
    case "workspace_template":
      return model.workspaces.find((record) => record.workspaceKey === reference.entityId) || null;
    case "workspace_assignment":
      return model.workspaces.find((record) => record.id === reference.entityId) || null;
    case "lead":
      return model.leads.find((record) => record.id === reference.entityId) || null;
    case "client":
      return model.clients.find((record) => record.id === reference.entityId) || null;
    case "project":
      return model.projects.find((record) => record.id === reference.entityId) || null;
    case "task":
      return model.tasks.find((record) => record.id === reference.entityId) || null;
    case "document":
      return model.documents.find((record) => record.id === reference.entityId) || null;
    case "billing_record":
      return model.billingRecords.find((record) => record.id === reference.entityId) || null;
    case "portal_access":
      return model.portalAccess.find((record) => record.id === reference.entityId) || null;
    case "role":
      return model.roleAssignments.find((record) => record.id === reference.entityId) || null;
    default:
      return null;
  }
}

function indexById<T extends { id: BusinessCenterEntityId }>(records: T[]) {
  return new Map(records.map((record) => [record.id, record] as const));
}

function resolveCompanyIdFromLead(lead: AdminLeadRecord, model: BusinessCenterUnifiedDataModel): BusinessCenterEntityId {
  if (lead.companyId) {
    const company = model.companies.find((entry) => entry.id === lead.companyId || entry.companySlug === lead.companyId);
    if (company) return company.id;
  }
  if (lead.clientId) {
    const client = model.clients.find((entry) => entry.id === lead.clientId);
    if (client) return resolveCompanyIdFromClient(client, model);
  }
  return model.companies.find((company) => company.companySlug === lead.leadId)?.id || lead.leadId || lead.id;
}

function resolveCompanyIdFromWorkspace(workspace: BusinessCenterWorkspaceRecord, model: BusinessCenterUnifiedDataModel): BusinessCenterEntityId {
  if (workspace.companyId) return workspace.companyId;
  const company = model.companies.find(
    (entry) => entry.companySlug === workspace.companySlug || entry.id === workspace.companyName || entry.companyName === workspace.companyName,
  );
  return company?.id || workspace.id;
}

function resolveCompanyIdFromClient(client: ClientRecord, model: BusinessCenterUnifiedDataModel): BusinessCenterEntityId {
  return model.companies.find((company) => company.companySlug === client.clientSlug)?.id || client.id;
}

function resolveCompanyIdFromProject(project: BusinessCenterProjectRecord, model: BusinessCenterUnifiedDataModel): BusinessCenterEntityId {
  if (project.clientId) {
    const client = model.clients.find((entry) => entry.id === project.clientId);
    if (client) return resolveCompanyIdFromClient(client, model);
  }
  return model.companies.find((company) => company.companySlug === project.brand)?.id || project.clientName || project.id;
}

function resolveCompanyIdFromTask(task: BusinessCenterTaskRecord, model: BusinessCenterUnifiedDataModel): BusinessCenterEntityId {
  if (task.clientId) {
    const client = model.clients.find((entry) => entry.id === task.clientId);
    if (client) return resolveCompanyIdFromClient(client, model);
  }
  if (task.projectId) {
    const project = model.projects.find((entry) => entry.id === task.projectId);
    if (project) return resolveCompanyIdFromProject(project, model);
  }
  return task.clientName || task.projectName || task.id;
}

function resolveCompanyIdFromDocument(document: BusinessCenterDocumentRecord, model: BusinessCenterUnifiedDataModel): BusinessCenterEntityId {
  if (document.clientId) {
    const client = model.clients.find((entry) => entry.id === document.clientId);
    if (client) return resolveCompanyIdFromClient(client, model);
  }
  if (document.projectId) {
    const project = model.projects.find((entry) => entry.id === document.projectId);
    if (project) return resolveCompanyIdFromProject(project, model);
  }
  return document.clientName || document.projectName || document.id;
}

function resolveCompanyIdFromBillingRecord(record: AdminPaymentRecord, model: BusinessCenterUnifiedDataModel): BusinessCenterEntityId {
  if (record.clientId) {
    const client = model.clients.find((entry) => entry.id === record.clientId);
    if (client) return resolveCompanyIdFromClient(client, model);
  }
  if (record.projectId) {
    const project = model.projects.find((entry) => entry.id === record.projectId);
    if (project) return resolveCompanyIdFromProject(project, model);
  }
  return record.leadName || record.invoiceNumber || record.id;
}
